import { useState } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Loader2, FileDown, Send, CalendarRange } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Exports = () => {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() === 0 ? 12 : today.getMonth());
  const [downloading, setDownloading] = useState(false);
  const [sending, setSending] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(
        `${API}/admin/exports/monthly?year=${year}&month=${month}`,
        { responseType: 'blob' }
      );
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `jpd_export_${year}-${String(month).padStart(2, '0')}.zip`;
      a.click();
      URL.revokeObjectURL(blobUrl);
      toast.success('Export téléchargé');
    } catch {
      toast.error("Erreur lors de l'export");
    } finally {
      setDownloading(false);
    }
  };

  const handleSendNow = async () => {
    if (!window.confirm('Envoyer immédiatement le CSV mensuel à jeff@jeffpdental.com ?')) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/exports/monthly/send-now`);
      if (res.data.ok) toast.success('CSV envoyé par email');
      else toast.warning("Email non envoyé (vérifiez la configuration SendGrid)");
    } catch {
      toast.error("Erreur lors de l'envoi");
    } finally {
      setSending(false);
    }
  };

  return (
    <AdminLayout title="Exports CSV" subtitle="Export mensuel des messages, alertes, statistiques et reportings">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="exports-page">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarRange className="h-5 w-5" />
              Export mensuel à la demande
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Génère un fichier ZIP contenant les messages échangés, les alertes de
              risque, les statistiques par client/labo et les reportings QCM du mois
              choisi. Les pièces jointes (photos, PDF, vidéos) ne sont pas incluses.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="year">Année</Label>
                <Input id="year" type="number" min="2024" max="2100" value={year} onChange={(e) => setYear(parseInt(e.target.value || '2026', 10))} data-testid="export-year" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="month">Mois</Label>
                <Input id="month" type="number" min="1" max="12" value={month} onChange={(e) => setMonth(parseInt(e.target.value || '1', 10))} data-testid="export-month" />
              </div>
            </div>
            <Button onClick={handleDownload} disabled={downloading} className="w-full" data-testid="export-download">
              {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />}
              Télécharger l'export ZIP
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send className="h-5 w-5" />
              Envoi automatique mensuel
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Le 1er de chaque mois à 06h00 (Europe/Paris), un email contenant le CSV
              du mois précédent est envoyé automatiquement à
              <strong> jeff@jeffpdental.com</strong>.
            </p>
            <div className="rounded-lg bg-muted p-3 text-xs space-y-1">
              <div><strong>QCM journalier :</strong> lun-ven 18h30</div>
              <div><strong>Relance "0 empreinte" :</strong> lun-ven 22h30</div>
              <div><strong>Relance lendemain :</strong> mar-sam 09h00</div>
              <div><strong>Export mensuel :</strong> 1er du mois à 06h00</div>
            </div>
            <Button onClick={handleSendNow} disabled={sending} variant="outline" className="w-full" data-testid="export-send-now">
              {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Envoyer le CSV maintenant
            </Button>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default Exports;
